import { IconAnchor } from '@tabler/icons-react';
import { AuthController } from '@ayocore/platform/identity/client/AuthController';
import type {
  NavigationGroupRef,
  NavigationItem,
} from '@ayocore/platform/client/types/navigation-item.type';
import logger from '@ayocore/platform/common/helpers/logger';
import { projectRepository } from '../rpc';

/** Функция регистрации навигации из module handle (bootstrap). */
type UseNavigationFn = (items: NavigationItem | NavigationItem[]) => void;

/**
 * Контроллер динамических пунктов меню «Проекты».
 *
 * После логина фетчит проекты и дорегистрирует по пункту на проект в группу
 * «Проекты» (label = project_no, url = /mws/projects/{id}).
 * Пункты без component — роутинг покрыт скрытым роутом /mws/projects/:id.
 *
 * Удалённые проекты скрываются через guard (реестр actualUrls): AppRuntime
 * не удаляет пункты навигации, поэтому пункт исчезнувшего проекта прячем.
 *
 * refresh() — публичный: дёргается после create/update проекта, чтобы меню
 * обновилось без перезагрузки страницы.
 */
export class ProjectsMenuController {
  private static instance: ProjectsMenuController | null = null;

  private useNavigation: UseNavigationFn | null = null;
  private group: NavigationGroupRef | null = null;
  /** URL-ы актуальных проектов; guard пунктов скрывает всё, чего здесь нет. */
  private actualUrls = new Set<string>();
  private subscribed = false;
  private loading = false;

  static getInstance(): ProjectsMenuController {
    if (!ProjectsMenuController.instance) {
      ProjectsMenuController.instance = new ProjectsMenuController();
    }
    return ProjectsMenuController.instance;
  }

  /**
   * Инициализация из bootstrap модуля: получает API регистрации навигации
   * и группу «Проекты». Подписывается на аутентификацию — проекты грузятся
   * только после логина (RPC до логина вернёт 401).
   */
  init(useNavigation: UseNavigationFn, group: NavigationGroupRef): void {
    this.useNavigation = useNavigation;
    this.group = group;

    if (this.subscribed) return;
    this.subscribed = true;

    const auth = AuthController.getInstance();
    let wasAuthenticated = auth.getSnapshot().isAuthenticated;

    auth.subscribe(() => {
      const isAuthenticated = auth.getSnapshot().isAuthenticated;
      // Реагируем только на переход «не залогинен → залогинен»
      if (isAuthenticated && !wasAuthenticated) {
        void this.refresh();
      }
      wasAuthenticated = isAuthenticated;
    });

    if (wasAuthenticated) {
      void this.refresh();
    }
  }

  /**
   * Перечитывает проекты и дорегистрирует пункты меню.
   * Повторные вызовы безопасны: пункты мёржатся по url (label обновляется),
   * пункты удалённых проектов скрываются guard-ом.
   */
  async refresh(): Promise<void> {
    if (!this.useNavigation || !this.group || this.loading) return;
    this.loading = true;
    try {
      const projects = await projectRepository.orderBy('project_no', 'asc').fetch();

      this.actualUrls = new Set(projects.map(project => `/mws/projects/${project.id}`));

      const items: NavigationItem[] = projects.map((project, index) => {
        const url = `/mws/projects/${project.id}`;
        return {
          group: this.group,
          icon: IconAnchor,
          label: project.project_no,
          url,
          // «Все проекты» в bootstrap имеет menuOrder 0 — проекты после него
          menuOrder: index + 1,
          guard: () => this.actualUrls.has(url),
        };
      });

      // Notify menu subscribers even when the last project disappeared:
      // actualUrls changed, so the previously registered guards must be re-evaluated.
      this.useNavigation(items);
    } catch (error) {
      logger.error('[ProjectsMenuController] Не удалось загрузить проекты для меню:', error);
    } finally {
      this.loading = false;
    }
  }
}
